// useSystemHealth — hook que centraliza los chequeos de salud del sistema.
// Combina chequeos del frontend (Supabase, flags, métricas) + el resultado
// de la edge function health-check (APIs externas + edge functions).
//
// Refresca automático cada 30s. Expone:
//   - servicios: array con cada chequeo
//   - resumen: { total, ok, warn, error }
//   - timestamp: última corrida
//   - refrescar(): fuerza recargar
//
// Diseñado para usar desde el header (indicador chico) y desde el dashboard
// (sección completa) sin duplicar pedidos.

import { useEffect, useRef, useState, useCallback } from 'react'
import { supabase } from './supabase'

const REFRESH_MS = 120_000  // 2 min (antes 30s): el health es un indicador; el sweep es pesado (~12 queries x corrida x varias instancias)
const CACHE_KEY = 'limpack_system_health_v1'
const CACHE_MAX_AGE_MS = 5 * 60_000  // 5 minutos: si el cache es más viejo, no lo mostramos

// Levanta el último estado conocido para evitar el "parpadeo" al cargar páginas nuevas
function loadCache() {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed.timestamp) return null
    const age = Date.now() - new Date(parsed.timestamp).getTime()
    if (age > CACHE_MAX_AGE_MS) return null
    return parsed
  } catch {
    return null
  }
}

function saveCache(servicios, timestamp) {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ servicios, timestamp }))
  } catch {}
}

export function useSystemHealth() {
  const cached = loadCache()
  const [servicios, setServicios] = useState(cached?.servicios || [])
  const [timestamp, setTimestamp] = useState(cached?.timestamp || null)
  // Si hay cache, NO mostramos "cargando" en el primer mount — usamos el cache mientras refrescamos en background
  const [cargando, setCargando] = useState(!cached)
  const [error, setError] = useState(null)
  const timerRef = useRef(null)

  const cargar = useCallback(async () => {
    setCargando(true)
    setError(null)
    try {
      // Chequeos en paralelo: 1) Supabase BD, 2) Storage, 3) panel.limpack.uy implícito (estás acá),
      // 4) edge function health-check (externos)
      const t0 = Date.now()
      const [
        bdResult,
        storageResult,
        flagsResult,
        metricasResult,
        edgeHealthResult,
      ] = await Promise.allSettled([
        chequearBd(),
        chequearStorage(),
        chequearFlags(),
        chequearMetricas(),
        chequearHealthCheck(),
      ])

      const propios = []

      // Panel.limpack.uy (estás viendo esto, así que está OK)
      propios.push({
        id: 'panel_limpack',
        nombre: 'panel.limpack.uy',
        categoria: 'sitios',
        estado: 'ok',
        critico: true,
        detalle: 'Estás en él',
      })

      // BD Supabase
      propios.push(mapResultadoBd('supabase_bd', 'Supabase · BD', 'backend', bdResult, true))
      propios.push(mapResultadoBd('supabase_auth', 'Supabase · Auth', 'backend', bdResult, true))
      propios.push(mapResultadoBd('supabase_storage', 'Supabase · Storage', 'backend', storageResult, false))

      // Flags y configs (informativo, no falla)
      if (flagsResult.status === 'fulfilled') {
        propios.push(...flagsResult.value)
      }

      // Métricas operativas
      if (metricasResult.status === 'fulfilled') {
        propios.push(...metricasResult.value)
      }

      // Externos vía edge function
      let externos = []
      if (edgeHealthResult.status === 'fulfilled' && edgeHealthResult.value) {
        externos = (edgeHealthResult.value.servicios || []).map(s => ({
          id: s.id,
          nombre: s.nombre,
          categoria: s.categoria,
          critico: s.critico,
          estado: estadoDe(s),
          latencia_ms: s.latencia_ms,
          detalle: s.error || `HTTP ${s.status}${s.latencia_ms ? ` · ${s.latencia_ms}ms` : ''}`,
        }))
      } else {
        externos.push({
          id: 'health_check_fn',
          nombre: 'health-check (edge function)',
          categoria: 'backend',
          critico: false,
          estado: 'warn',
          detalle: 'No se pudo invocar — chequeos externos no disponibles',
        })
      }

      const todos = [...propios, ...externos]
      setServicios(todos)
      setTimestamp(new Date().toISOString())
    } catch (err) {
      setError(err.message || 'Error desconocido')
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    cargar()
    timerRef.current = setInterval(cargar, REFRESH_MS)
    return () => clearInterval(timerRef.current)
  }, [cargar])

  const resumen = (() => {
    let ok = 0, warn = 0, err = 0
    let criticosError = 0
    for (const s of servicios) {
      if (s.estado === 'ok') ok++
      else if (s.estado === 'warn') warn++
      else { err++; if (s.critico) criticosError++ }
    }
    let estadoGlobal = 'ok'
    if (criticosError > 0) estadoGlobal = 'error'
    else if (err > 0 || warn > 0) estadoGlobal = 'warn'
    return { total: servicios.length, ok, warn, error: err, criticosError, estadoGlobal }
  })()

  return {
    servicios,
    resumen,
    timestamp,
    cargando,
    error,
    refrescar: cargar,
  }
}

// ─── Chequeos individuales ───

async function chequearBd() {
  const t0 = Date.now()
  const { data, error } = await supabase.from('app_config').select('key').limit(1)
  return { ok: !error, latencia_ms: Date.now() - t0, error: error?.message }
}

async function chequearStorage() {
  const t0 = Date.now()
  try {
    const { data, error } = await supabase.storage.listBuckets()
    return { ok: !error, latencia_ms: Date.now() - t0, error: error?.message, buckets: data?.length || 0 }
  } catch (err) {
    return { ok: false, latencia_ms: Date.now() - t0, error: err.message }
  }
}

async function chequearFlags() {
  const flags = []
  // beta_cerrada
  const { data: betaData } = await supabase.from('app_config').select('value').eq('key', 'beta_cerrada').maybeSingle()
  if (betaData) {
    const activa = betaData.value === 'true' || betaData.value === true
    flags.push({
      id: 'flag_beta_cerrada',
      nombre: 'Beta cerrada',
      categoria: 'flags',
      estado: activa ? 'warn' : 'ok',
      detalle: activa ? '🟡 ACTIVA — solo whitelist puede registrarse' : '🟢 Abierta',
      critico: false,
    })
  }
  return flags
}

async function chequearMetricas() {
  const hoy = new Date().toISOString().slice(0, 10)
  const metricas = []

  // Entregas programadas hoy
  const { count: entregasHoy } = await supabase
    .from('entregas')
    .select('id', { count: 'exact', head: true })
    .eq('fecha_programada', hoy)
  metricas.push({
    id: 'metrica_entregas_hoy',
    nombre: 'Entregas programadas hoy',
    categoria: 'metricas',
    estado: 'ok',
    detalle: `${entregasHoy ?? 0}`,
    critico: false,
    valor: entregasHoy ?? 0,
  })

  // Cobros pendientes
  const { count: cobrosPend } = await supabase
    .from('pagos')
    .select('id', { count: 'exact', head: true })
    .eq('estado', 'pendiente')
  metricas.push({
    id: 'metrica_cobros_pendientes',
    nombre: 'Cobros pendientes',
    categoria: 'metricas',
    estado: (cobrosPend ?? 0) > 10 ? 'warn' : 'ok',
    detalle: `${cobrosPend ?? 0}`,
    critico: false,
    valor: cobrosPend ?? 0,
  })

  // Stock crítico (productos bajo mínimo físico) — usamos RPC porque PostgREST
  // .lt() no soporta comparar columna vs columna (siempre trata el 2do arg como literal).
  const { data: stockCritico } = await supabase.rpc('contar_productos_stock_critico')
  metricas.push({
    id: 'metrica_stock_critico',
    nombre: 'Stock crítico (productos)',
    categoria: 'metricas',
    estado: (stockCritico ?? 0) > 0 ? 'warn' : 'ok',
    detalle: `${stockCritico ?? 0}`,
    critico: false,
    valor: stockCritico ?? 0,
  })

  // Reservas > stock físico (sobrereserva — overselling potencial)
  // Productos donde reservamos más de lo que tenemos físicamente.
  // Causas: bug en triggers o reservas viejas que quedaron colgadas.
  // Usamos la vista vw_productos_stock que ya calcula disponible = actual - reservado.
  const { count: sobreReserva } = await supabase
    .from('vw_productos_stock')
    .select('id', { count: 'exact', head: true })
    .eq('activo', true)
    .is('deleted_at', null)
    .lt('stock_disponible', 0)
  metricas.push({
    id: 'metrica_stock_sobrereserva',
    nombre: 'Productos sobre-reservados',
    categoria: 'metricas',
    estado: (sobreReserva ?? 0) > 0 ? 'error' : 'ok',
    detalle: (sobreReserva ?? 0) > 0
      ? `${sobreReserva} producto(s) con reservas > stock físico — revisar`
      : 'todos consistentes',
    critico: true,
    valor: sobreReserva ?? 0,
  })

  // Consultas comerciales nuevas
  const { count: consultasNuevas } = await supabase
    .from('consultas_comerciales')
    .select('id', { count: 'exact', head: true })
    .eq('estado', 'nuevo')
  metricas.push({
    id: 'metrica_consultas_nuevas',
    nombre: 'Consultas B2B nuevas',
    categoria: 'metricas',
    estado: 'ok',
    detalle: `${consultasNuevas ?? 0}`,
    critico: false,
    valor: consultasNuevas ?? 0,
  })

  // Compras pendientes de aprobar (e-Facturas recibidas)
  const { count: comprasPend } = await supabase
    .from('compras')
    .select('id', { count: 'exact', head: true })
    .eq('estado', 'pendiente')
  metricas.push({
    id: 'metrica_compras_pendientes',
    nombre: 'Compras pendientes aprobar',
    categoria: 'metricas',
    estado: 'ok',
    detalle: `${comprasPend ?? 0}`,
    critico: false,
    valor: comprasPend ?? 0,
  })

  // Sugerencias Claude últimas 24h (llamadas reales, sin contar skips)
  const desde24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
  const { count: aiLlamadas } = await supabase
    .from('crm_ai_usage_log')
    .select('id', { count: 'exact', head: true })
    .gte('created_at', desde24h)
    .is('motivo_skip', null)
  const { count: aiSkips } = await supabase
    .from('crm_ai_usage_log')
    .select('id', { count: 'exact', head: true })
    .gte('created_at', desde24h)
    .not('motivo_skip', 'is', null)
  metricas.push({
    id: 'metrica_ai_sugerencias',
    nombre: 'Sugerencias Claude (24h)',
    categoria: 'metricas',
    estado: 'ok',
    detalle: `${aiLlamadas ?? 0} llamadas · ${aiSkips ?? 0} skipeadas`,
    critico: false,
    valor: aiLlamadas ?? 0,
  })

  return metricas
}

// v2 (2026-07-13): antes llamaba a la edge fn health-check en vivo (2-12s por request).
// Ahora lee de monitor_externos_log — el cron `monitor-externos-cada-10min` mantiene la
// tabla actualizada server-side. Latencia: <100ms en vez de 2-12s.
// Datos ~10 min de antigüedad max, más que suficiente para el UI.
async function chequearHealthCheck() {
  // Trae el último snapshot de cada servicio externo monitoreado
  const { data, error } = await supabase
    .from('monitor_externos_log')
    .select('servicio, indicator, description, created_at, incidents_activos, mantenimientos')
    .order('created_at', { ascending: false })
    .limit(20)
  if (error) throw error

  // Deduplicar por servicio: quedarnos con el más reciente de cada uno
  const seen = new Set()
  const ultimos = []
  for (const row of (data || [])) {
    if (seen.has(row.servicio)) continue
    seen.add(row.servicio)
    ultimos.push(row)
  }

  // Adaptar al formato que espera el consumidor (mismo shape que la edge fn):
  // { externos: [{ nombre, estado, detalle, latencia_ms }] }
  return {
    externos: ultimos.map(row => ({
      nombre: row.servicio,
      estado: row.indicator === 'none' ? 'ok' : (row.indicator === 'critical' || row.indicator === 'major' ? 'error' : 'warn'),
      detalle: row.description || row.indicator,
      latencia_ms: null,
      incidents_activos: row.incidents_activos || 0,
      mantenimientos: row.mantenimientos || 0,
      ultima_actualizacion: row.created_at,
    })),
    edge_functions: [],  // ya no se chequean en vivo — hay checkeadores dedicados server-side
    fuente: 'monitor_externos_log',
  }
}

// ─── Helpers ───

function mapResultadoBd(id, nombre, categoria, result, critico) {
  if (result.status !== 'fulfilled') {
    return { id, nombre, categoria, critico, estado: 'error', detalle: result.reason?.message || 'Error' }
  }
  const r = result.value
  return {
    id,
    nombre,
    categoria,
    critico,
    estado: r.ok ? 'ok' : 'error',
    latencia_ms: r.latencia_ms,
    detalle: r.error || `${r.latencia_ms}ms${r.buckets !== undefined ? ` · ${r.buckets} buckets` : ''}`,
  }
}

function estadoDe(servicio) {
  if (servicio.ok) return 'ok'
  // Si la latencia es alta o el status es 5xx → warn/error
  if (servicio.status >= 500) return 'error'
  if (servicio.status === 0) return 'error'  // sin respuesta
  return 'warn'
}
